import { apiService } from './api';
import agreements from '../../infrastructure/src/lambdas/webui/handlers/legal-agreements.json';

export interface LegalDocumentVersion {
  id: string;
  title: string;
  version: string;
  action: 'accepted' | 'acknowledged' | 'incorporated';
  url: string;
}
export interface LegalRelease {
  schemaVersion: 1;
  bundleId: string;
  revisionDate: string;
  documents: LegalDocumentVersion[];
  prompts: {
    statement: string;
    acknowledgements: { noColdemail: string; ownedBrands: string; transactional: string };
  };
  signedDpa: { url: string; documentDate: string; signerName: string; signerTitle: string } | null;
}
export interface LegalAcceptance {
  acceptanceId: string;
  userId: string;
  workspaceId: string;
  acceptedAt: string;
  bundleId: string;
  capacity: 'organization';
  actorName: string;
  actorEmail?: string;
  documents: LegalDocumentVersion[];
}
export interface LegalDocumentStatus {
  manifest: LegalRelease;
  legalWorkspaceId: string;
  accepted: boolean;
  canAcceptForOrganization: boolean;
  history: LegalAcceptance[];
  aiContentVerificationEnabled: boolean | null;
}
export const legalQueryKey = (userId?: string, workspaceId?: string) => ['legal-documents', userId, workspaceId];
export const getLegalDocuments = (workspaceId: string): Promise<LegalDocumentStatus> => apiService.makeRequest('/account/legal', {}, workspaceId);
export const acceptLegalDocuments = (status: LegalDocumentStatus, workspaceId: string) => apiService.makeRequest('/account/legal/accept', {
  method: 'POST',
  body: JSON.stringify({ bundleId: status.manifest.bundleId, agreed: true }),
}, workspaceId);

export async function getSignupLegalRelease(): Promise<LegalRelease> {
  return agreements as LegalRelease;
}

export async function downloadAcceptanceReceipt(bundleId: string, workspaceId: string) {
  await downloadLegalFile(`/account/legal/receipt?bundleId=${encodeURIComponent(bundleId)}`, workspaceId);
}

export async function downloadCompanyDpa(workspaceId: string) {
  await downloadLegalFile('/account/legal/dpa', workspaceId);
}

async function downloadLegalFile(endpoint: string, workspaceId: string) {
  const result = await apiService.makeRequest(endpoint, {}, workspaceId);
  const bytes = Uint8Array.from(atob(result.contentBase64), character => character.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = result.fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function formatLegalDate(value: string, includeTime = false): string {
  const date = new Date(value.length === 10 ? `${value}T00:00:00.000Z` : value);
  if (!Number.isFinite(date.getTime())) return value;
  const options: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' };
  if (includeTime) Object.assign(options, { hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' });
  return new Intl.DateTimeFormat('en-GB', options).format(date) + (includeTime ? ' UTC' : '');
}
